import { AfterViewInit, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ballotListRoute } from '../route-names';
import { BallotListService } from '../ballot-list/ballot-list.service'; 
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { SessionService } from '../Services/session.service';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { BallotMarkingService } from '../Services/ballot-marking.service';
import { Ballot, Choice, ballotMarkingMetadata , ballotMarkingData} from '../ballot/backend-types';
import { ConfirmationType } from '../backend-types/misc-types';

@Component({
    selector: 'app-print-page',
    templateUrl: './print-page.component.html',
    styleUrls: ['./print-page.component.scss'],
    standalone: false
})
export class PrintPageComponent implements AfterViewInit, OnDestroy {
  confirmationType: ConfirmationType | undefined;
  confirmationId: string | undefined;
  routeName = ""
  ballotPdfURL: string | undefined;
  envelopePdfURL: string | undefined;
  ballotArray: Blob = new Blob();
  envelopeArray: Blob = new Blob();
  selectedDocumentNameLocalize: string = $localize`BALLOT`;
  selectedDocumentName: string = "BALLOT";
  ballotPrinted: boolean = false;
  envelopePrinted: boolean = false;
  emptyBlob = new Blob();
  markedBallotReady: boolean = false;
  isLoadingPdf: boolean = true;

  constructor(
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly ballotMarkingService: BallotMarkingService,
    private readonly ballotListService: BallotListService,
    private announcer: LiveAnnouncer,
    private pdfService: NgxExtendedPdfViewerService,
    @Inject(LOCALE_ID) readonly locale: string
  ) { 

    this.confirmationId = history.state.confirmationId;
    this.confirmationType = history.state.confirmationType;
    this.ballotPdfURL = ballotListService.getBallotURL() ?? "";
    let ballotPromise = fetch(this.ballotPdfURL, { headers: { Accept: "application/pdf" } });
    this.envelopePdfURL = ballotListService.getEnvelopeURL() ?? "";
    let envelopePromise = fetch(this.envelopePdfURL, { headers: { Accept: "application/pdf" } });
    Promise.allSettled([ballotPromise, envelopePromise]).then(async ([ballotResponse, envelopeResponse]) => {
      if(ballotResponse.status === 'fulfilled') {
        this.ballotArray = await ballotResponse.value.blob();
      }
      if(envelopeResponse.status === 'fulfilled') {
        this.envelopeArray = await envelopeResponse.value.blob();
      }
      let ballot: Ballot = this.ballotListService.getBallots()[0];
      const resultsArray = this.processBallotQuestions(ballot);

      this.ballotMarkingService.ballotToPdf(resultsArray, this.ballotArray)
      .then((markedBallotArray) => {
        this.ballotArray = markedBallotArray ?? this.emptyBlob
        this.markedBallotReady = true;
        this.isLoadingPdf = false;
      })
      .catch((error) => {
        this.isLoadingPdf = false; 
        console.error("Error obtaining marked ballot from service.", error);
      });

    })
  }

  ngAfterViewInit() {
    this.routeName = this.router.url
    this.resetFocus()
  }

  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')
  
    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
      focusFieldMobile.focus()
    }
  }

  get hasMoreBallots(): boolean {
    return this.ballotListService.getBallots().length > 1
  }

  continue() {
    this.router.navigate(ballotListRoute)
  }

  print(): void {
    window.print()
  }

  exit(): void {
    this.sessionService.finishedVoting()
  }

  public showBallot() {
    this.selectedDocumentName = "BALLOT"
    this.selectedDocumentNameLocalize = $localize`BALLOT`;
  }

  public showEnvelope() {
    this.selectedDocumentNameLocalize = $localize`ENVELOPES`;
    this.selectedDocumentName = "ENVELOPES";
  }
  printActual(){
    if(this.selectedDocumentName.toUpperCase() == "BALLOT"){
      this.ballotPrinted = true;
    }
    if (this.selectedDocumentName.toUpperCase() == "ENVELOPES"){
      this.envelopePrinted = true;
    }
    this.pdfService.print()
  }

  pdfsNotPrinted(): boolean {
    if(this.envelopeArray) {
      return !(this.ballotPrinted && this.envelopePrinted);
    } else {
      return !this.ballotPrinted;
    }
  }

  processBallotQuestions(ballot: Ballot): ballotMarkingData[] {
    const questions = ballot?.contest_list;
    const resultsArray: ballotMarkingData[] = [];
  
    questions?.forEach(question => {
      if (question.isRCV) {
        const rankedChoices = question.rankedStatements;
        rankedChoices.forEach(ranked => {
          resultsArray.push(...this.processBallotChoices(ranked.choices));
        });
      } else {
        const nonRCV = question.statement?.choices;
        if (nonRCV) {
          resultsArray.push(...this.processBallotChoices(nonRCV));
        }
      }
    });  
    return resultsArray;
  }

  processBallotChoices(choices: Choice[]):ballotMarkingData[] {
    const processedChoices: ballotMarkingData[] = [];
    choices.forEach(choice => {
      if(choice.selected){
        let choiceMetaData: ballotMarkingMetadata = {}
        if(choice.ballotMarkingMetadata){
          choiceMetaData = choice.ballotMarkingMetadata
        }
        let writeIndescription = ''
        let isChoiceWriteIn = false
        if(choice.isWriteIn){
          if(choice.description.includes(':')) {
            writeIndescription = choice.description.split(':')[1];
          } else {
            writeIndescription = choice.description;
          }
          isChoiceWriteIn = true
        }
        const choiceData: ballotMarkingData =
          {
            description:  writeIndescription,
            isWriteIn: isChoiceWriteIn,
            ballotMarkingMetadata:choiceMetaData
        }
      processedChoices.push(choiceData);
      }
    });  
    return processedChoices;
  }

  ngOnDestroy() {
    this.ballotArray = this.emptyBlob
    this.envelopeArray = this.emptyBlob
  }
}